<template>
    <v-card flat>
        <v-card-title class="d-flex align-center">
            Logs
            <v-spacer></v-spacer>
            <v-btn v-if="tab === 'parsed'" icon="mdi-refresh" variant="text" size="small" @click="fetchSessions()" :loading="loading"></v-btn>
            <v-btn v-if="tab === 'parsed'" icon="mdi-upload" variant="text" size="small" @click="triggerUpload" :loading="uploading" color="primary"></v-btn>
            <v-btn v-if="tab === 'raw'" icon="mdi-refresh" variant="text" size="small" @click="fetchRawSessions" :loading="loadingRaw" color="primary"></v-btn>
            
            <input ref="fileInput" type="file" accept=".ndjson" style="display: none" @change="uploadFile" />
        </v-card-title>
        
        <v-tabs v-model="tab" density="compact" color="primary" class="mb-4">
            <v-tab value="parsed">Parsed DPS Logs</v-tab>
            <v-tab value="raw">Raw Captures</v-tab>
        </v-tabs>

        <v-tabs-window v-model="tab">
            <v-tabs-window-item value="parsed">
                <v-card-text>
                    <v-table density="compact">
                        <thead>
                            <tr>
                                <th class="text-left">Session Name</th>
                                <th class="text-left">Label</th>
                                <th class="text-left">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in sessions" :key="item.tableName">
                                <td>
                                    {{ parseTableName(item.tableName) }}
                                    <v-chip v-if="currentSessionName === item.tableName" color="success" size="x-small" class="ml-2">
                                        Loaded
                                    </v-chip>
                                    <v-chip v-if="item.isActive" color="primary" size="x-small" class="ml-2">
                                        Active
                                    </v-chip>
                                    <v-chip v-if="item.isArchived" color="grey" size="x-small" class="ml-2">
                                        Archived
                                    </v-chip>
                                </td>
                                <td>
                                    <div v-if="!item.isArchived" class="d-flex align-center cursor-pointer" @click="openLabelDialog(item)">
                                        <span v-if="item.label" class="text-body-2">{{ item.label }}</span>
                                        <span v-else class="text-caption text-grey font-italic">Add Label...</span>
                                        <v-icon icon="mdi-pencil" size="x-small" color="grey" class="ml-2 opacity-0 hover-show"></v-icon>
                                    </div>
                                    <span v-else class="text-caption text-grey">-</span>
                                </td>
                                <td>
                                    <v-btn color="primary" size="x-small" @click="$emit('load-session', item.tableName, item.eventCount)" class="mr-2">
                                        Load
                                    </v-btn>
                                    <v-btn color="error" size="x-small" variant="text" icon="mdi-delete" @click="confirmDelete(item.tableName)" :disabled="item.isActive || item.isArchived">
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-if="sessions.length === 0 && !loading">
                                <td colspan="3" class="text-center text-medium-emphasis">No logs found</td>
                            </tr>
                        </tbody>
                    </v-table>
                    <!-- 10 logs per page, newest first; only the shown page is read from the database -->
                    <div v-if="pageCount > 1" class="d-flex align-center justify-center mt-2" style="gap: 8px">
                        <v-pagination v-model="page" :length="pageCount" :total-visible="7" density="compact"
                            :disabled="loading" />
                        <span class="text-caption text-medium-emphasis">{{ total }} logs</span>
                    </div>
                </v-card-text>
            </v-tabs-window-item>

            <v-tabs-window-item value="raw">
                <v-card-text>
                    <v-table density="compact">
                        <thead>
                            <tr>
                                <th class="text-left">Capture Table</th>
                                <th class="text-left">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in rawSessions" :key="item">
                                <td>
                                    {{ parseTableName(item) }}
                                    <v-chip v-if="currentReplaySession === item" color="secondary" size="x-small" class="ml-2">
                                        Replaying
                                    </v-chip>
                                </td>
                                <td>
                                    <v-btn color="secondary" size="x-small" @click="triggerReplay(item)" :loading="startingReplay === item" prepend-icon="mdi-play">
                                        Live Replay
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-if="rawSessions.length === 0 && !loadingRaw">
                                <td colspan="2" class="text-center text-medium-emphasis">No raw captures found</td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-tabs-window-item>
        </v-tabs-window>
        <v-dialog v-model="labelDialog" max-width="400">
            <v-card>
                <v-card-title>Edit Label</v-card-title>
                <v-card-text>
                    <v-text-field v-model="editingLabel" label="Label" autofocus
                        @keydown.enter="saveLabel"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="labelDialog = false">Cancel</v-btn>
                    <v-btn color="primary" @click="saveLabel">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
                <v-card-title>Confirm Delete</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this log session? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
                    <v-btn color="error" @click="executeDelete" :loading="deleting">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script lang="ts">
import { defineComponent, inject, onMounted, onUnmounted, ref, computed, watch } from 'vue';

type SessionInfo = {
    tableName: string;
    startTime: number;
    label?: string;
    eventCount?: number;
    isActive?: boolean;
    isArchived?: boolean;
};

export default defineComponent({
    name: "LogList",
    emits: ['load-session'],
    setup() {
        const currentSessionName = inject('currentSessionName') as any;
        const tab = ref("parsed");
        
        const sessions = ref<SessionInfo[]>([]);
        const loading = ref(false);

        // Paging: the server lists names cheaply and only reads (counts,
        // labels) the requested page, newest first.
        const PAGE_SIZE = 10;
        const page = ref(1);
        const total = ref(0);
        const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));
        let requestSeq = 0;

        const rawSessions = ref<string[]>([]);
        const loadingRaw = ref(false);
        const startingReplay = ref("");
        const currentReplaySession = ref("");

        // File upload
        const fileInput = ref<HTMLInputElement | null>(null);
        const uploading = ref(false);

        // Label Editing
        const labelDialog = ref(false);
        const editingItem = ref<SessionInfo | null>(null);
        const editingLabel = ref("");

        // Deleting
        const deleteDialog = ref(false);
        const deletingItem = ref("");
        const deleting = ref(false);

        const fetchSessions = async () => {
            const seq = ++requestSeq;
            loading.value = true;
            try {
                // Add timestamp to prevent caching
                const res = await fetch(`/api/sessions?page=${page.value}&pageSize=${PAGE_SIZE}&_t=${Date.now()}`);
                if (!res.ok) {
                    throw new Error(`Failed to fetch sessions: ${res.status}`);
                }
                const data = await res.json();
                if (seq !== requestSeq) return; // a newer page request won
                sessions.value = data?.sessions || [];
                total.value = data?.total || 0;
                // Deletes can shrink the list under the current page.
                if (page.value > pageCount.value) page.value = pageCount.value;
            } catch (e) {
                console.error(e);
            } finally {
                if (seq === requestSeq) loading.value = false;
            }
        };
        watch(page, () => { void fetchSessions(); });

        const fetchRawSessions = async () => {
            loadingRaw.value = true;
            try {
                const res = await fetch(`/api/raw_sessions?_t=${Date.now()}`);
                if (!res.ok) throw new Error("Failed to fetch raw sessions");
                rawSessions.value = await res.json() || [];
            } catch (e) {
                console.error(e);
            } finally {
                loadingRaw.value = false;
            }
        };

        const triggerReplay = async (tableName: string) => {
            startingReplay.value = tableName;
            try {
                const res = await fetch('/api/replay', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ target: tableName })
                });

                if (!res.ok) throw new Error("Failed to trigger replay");
                currentReplaySession.value = tableName;
            } catch (e) {
                console.error("Replay triggered error:", e);
                alert(`Replay failed: ${e}`);
            } finally {
                startingReplay.value = "";
            }
        };

        const parseTableName = (name: string) => {
            // session_20260127_150405 -> 2026-01-27 15:04:05
            const match = name.match(/^(?:raw_)?session_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})(?:.*)?$/);
            if (match) {
                let parsed = `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}:${match[6]}`;
                if (name.includes('_replay')) {
                    parsed += " (Replay)";
                }
                return parsed;
            }
            return name;
        };

        const triggerUpload = () => {
            fileInput.value?.click();
        };

        const uploadFile = async (event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files?.length) return;

            const file = target.files[0];
            uploading.value = true;

            try {
                const formData = new FormData();
                formData.append('file', file);

                console.log(`[Upload] Uploading file: ${file.name} (${file.size} bytes)`);

                const res = await fetch('/api/upload_log', {
                    method: 'POST',
                    body: formData,
                });

                if (!res.ok) {
                    const errorText = await res.text();
                    throw new Error(`Upload failed: ${res.status} - ${errorText}`);
                }

                const data = await res.json();
                console.log(`[Upload] Success! Session ID: ${data.sessionId}`);
                alert(`File uploaded successfully!\nSession: ${data.sessionId}`);

                // Refresh the session list from the first page
                if (page.value !== 1) page.value = 1;
                else await fetchSessions();
            } catch (e) {
                console.error('[Upload] Error:', e);
                alert(`Upload failed: ${e}`);
            } finally {
                uploading.value = false;
                // Reset file input so the same file can be uploaded again
                if (target) target.value = '';
            }
        };

        const openLabelDialog = (item: SessionInfo) => {
            editingItem.value = item;
            editingLabel.value = item.label || "";
            labelDialog.value = true;
        };

        const saveLabel = async () => {
            if (!editingItem.value) return;
            const tableName = editingItem.value.tableName;
            const label = editingLabel.value;

            try {
                const res = await fetch('/api/sessions/label', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: tableName, label }),
                });

                if (!res.ok) throw new Error("Failed to save label");

                // Update locally or refresh
                editingItem.value.label = label;
                labelDialog.value = false;
            } catch (e) {
                console.error(e);
            }
        };

        const confirmDelete = (tableName: string) => {
            deletingItem.value = tableName;
            deleteDialog.value = true;
        };

        const executeDelete = async () => {
            if (!deletingItem.value) return;
            deleting.value = true;
            try {
                const res = await fetch(`/api/sessions?id=${deletingItem.value}`, {
                    method: 'DELETE',
                });

                if (!res.ok) throw new Error("Failed to delete session");

                // Optimistic update
                sessions.value = sessions.value.filter(s => s.tableName !== deletingItem.value);

                deleteDialog.value = false;

                // Fetch in background to sync
                fetchSessions();
            } catch (e) {
                console.error(e);
            } finally {
                deleting.value = false;
            }
        };

        onMounted(() => {
            fetchSessions();
            fetchRawSessions();
            window.addEventListener('replay-finished', fetchSessions);
        });

        onUnmounted(() => {
            window.removeEventListener('replay-finished', fetchSessions);
        });

        return {
            tab,
            currentSessionName,
            sessions,
            loading,
            fetchSessions,
            page,
            total,
            pageCount,
            
            rawSessions,
            loadingRaw,
            startingReplay,
            currentReplaySession,
            fetchRawSessions,
            triggerReplay,

            parseTableName,

            fileInput,
            uploading,
            triggerUpload,
            uploadFile,

            labelDialog,
            editingLabel,
            openLabelDialog,
            saveLabel,

            deleteDialog,
            deleting,
            confirmDelete,
            executeDelete,
        };
    },
});
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.hover-show {
    opacity: 0;
    transition: opacity 0.2s;
}

tr:hover .hover-show {
    opacity: 1;
}
</style>


